/**
 * DeleteMessageDialog - Diálogo de confirmação para exclusão de mensagens
 * (Modal shared — F71/F72: portal, ESC, tema claro fixo, Cancelar secondary)
 */

import React, { useState } from 'react';
import type { ConversationMessage } from '@troia-v3/types';
import { Modal, ModalHeader, ModalContent, ModalFooter } from '@/shared/components/ui/Modal';
import { Button } from '@/shared/components/ui/Button';
import { RadioGroup } from '@/shared/components/ui/RadioGroup';
import { Icon } from '@/shared/icons'
interface DeleteMessageDialogProps {
  message: ConversationMessage;
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (deleteForEveryone: boolean) => void;
  isLoading?: boolean;
  canDeleteForEveryone?: boolean;
}

const DeleteMessageDialog: React.FC<DeleteMessageDialogProps> = ({
  message,
  isOpen,
  onClose,
  onConfirm,
  isLoading = false,
  canDeleteForEveryone = true,
}) => {
  const [deleteForEveryone, setDeleteForEveryone] = useState(false);

  const getMessagePreview = () => {
    // Extract text content from message
    const textContent = message.content.find(
      (c) => c.type === 'text' && 'text' in c
    );

    if (textContent && 'text' in textContent) {
      return textContent.text.substring(0, 80) + (textContent.text.length > 80 ? '...' : '');
    }

    // Check for other content types
    const imageContent = message.content.find(c => c.type === 'image');
    if (imageContent) return '🖼️ Imagem';

    const videoContent = message.content.find(c => c.type === 'video');
    if (videoContent) return '🎥 Vídeo';

    const audioContent = message.content.find(c => c.type === 'audio');
    if (audioContent) return '🎵 Áudio';

    const documentContent = message.content.find(c => c.type === 'document');
    if (documentContent) return '📄 Documento';

    return 'Mensagem';
  };

  const handleConfirm = () => {
    onConfirm(deleteForEveryone);
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} maxWidth="md" closeOnEscape={!isLoading}>
      <ModalHeader title="Excluir mensagem" onClose={isLoading ? undefined : onClose}>
        <div className="w-9 h-9 rounded-[9px] flex items-center justify-center flex-none order-first"
          style={{ backgroundColor: '#fef2f2', color: '#dc2626' }}
        >
          <Icon name="trash" className="w-5 h-5" />
        </div>
      </ModalHeader>

      <ModalContent>
        {/* Warning */}
        <div className="flex items-start gap-3 mb-4 p-3 bg-amber-50 border border-amber-200 rounded-lg">
          <Icon name="alert-triangle" className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
          <div className="flex-1">
            <p className="text-sm font-medium text-amber-900 mb-1">
              Esta ação não pode ser desfeita
            </p>
            <p className="text-sm text-amber-700">
              A mensagem será permanentemente excluída.
            </p>
          </div>
        </div>

        {/* Message Preview */}
        <div className="mb-4">
          <p className="text-sm mb-2" style={{ color: '#64748b' }}>
            Mensagem a ser excluída:
          </p>
          <div
            className="border rounded-lg p-3"
            style={{
              backgroundColor: '#f1f5f9',
              borderColor: '#e5e7eb',
            }}
          >
            <p className="text-sm" style={{ color: '#1e293b' }}>
              {getMessagePreview()}
            </p>
            <p className="text-xs mt-1" style={{ color: '#64748b' }}>
              Enviada em {new Date(message.sentAt).toLocaleString('pt-BR')}
            </p>
          </div>
        </div>

        {/* Delete Options — RadioGroup shared (variant cards) */}
        {canDeleteForEveryone && (
          <RadioGroup
            label="Opções de exclusão:"
            variant="cards"
            value={deleteForEveryone ? 'everyone' : 'me'}
            onChange={(value) => setDeleteForEveryone(value === 'everyone')}
            options={[
              {
                value: 'me',
                label: 'Excluir para mim',
                description: 'A mensagem será removida apenas para você',
                disabled: isLoading,
              },
              {
                value: 'everyone',
                label: 'Excluir para todos',
                description: 'A mensagem será removida para todos os participantes',
                disabled: isLoading,
              },
            ]}
          />
        )}
      </ModalContent>

      <ModalFooter>
        <Button
          variant="secondary"
          onClick={onClose}
          disabled={isLoading}
          data-testid="delete-message-cancel"
        >
          Cancelar
        </Button>
        <Button
          variant="error"
          onClick={handleConfirm}
          disabled={isLoading}
          loading={isLoading}
          icon={isLoading ? undefined : 'trash'}
          data-testid="delete-message-confirm"
        >
          {isLoading
            ? 'Excluindo...'
            : deleteForEveryone ? 'Excluir para todos' : 'Excluir para mim'}
        </Button>
      </ModalFooter>
    </Modal>
  );
};

export default DeleteMessageDialog;
